import React, { useEffect, useState, useMemo } from "react";
import { Table, Button, Form, Modal, Badge, Spinner, Row, Col, InputGroup } from "react-bootstrap";
import api from "../../api";
import Can from "../../components/Can";
import { toast } from "react-toastify";
import "./PerformanceProfileManagementPage.css";
import PerformanceProfileEditor from "../../components/performance/PerformanceProfileEditor";
import EmptyState from "../../components/common/EmptyState";
import { FaUserCog } from "react-icons/fa";

// Phase 5, §7/§23/§32. Performance profiles — never auto-activated; Admin
// explicitly configures target + metrics + weightage, validates, then
// activates. Resolution-preview lets an Admin confirm which profile would
// actually apply to a given user/context before relying on it.
const initialForm = { code: "", name: "", description: "", role_id: "", department_id: "", project_id: "", project_module_id: "", user_id: "", allow_ranking: false };

export default function PerformanceProfileManagementPage() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState(null);

  const [previewCtx, setPreviewCtx] = useState({ user_id: "", role_id: "", department_id: "", project_id: "", project_module_id: "" });
  const [previewResult, setPreviewResult] = useState(null);
  const [search, setSearch] = useState("");

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const res = await api.get("/performance-profiles", { params: { pageSize: 100 } });
      setProfiles(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch profiles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfiles(); }, []);

  const handleCreate = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.warning("Code and name are required"); return; }
    setSaving(true);
    try {
      await api.post("/performance-profiles", form);
      toast.success("Profile created — add metrics before activating");
      setShowModal(false);
      setForm(initialForm);
      fetchProfiles();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create profile");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/performance-profiles/${p.id}/status`, { is_active: !p.is_active });
      toast.success(p.is_active ? "Profile deactivated" : "Profile activated");
      fetchProfiles();
    } catch (err) {
      toast.error(err.response?.data?.message || "Cannot activate: weightage or configuration is invalid");
    }
  };

  const runPreview = async () => {
    try {
      const res = await api.post("/performance-profiles/preview-resolution", previewCtx);
      setPreviewResult(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Preview failed");
    }
  };

  const visibleProfiles = useMemo(() => {
    if (!search) return profiles;
    const q = search.trim().toLowerCase();
    return profiles.filter((p) => p.code?.toLowerCase().includes(q) || p.name?.toLowerCase().includes(q));
  }, [profiles, search]);

  const stats = useMemo(() => ({
    total: profiles.length,
    active: profiles.filter((p) => p.is_active).length,
    ranking: profiles.filter((p) => p.allow_ranking).length,
  }), [profiles]);

  return (
    <div className="unit-page perfprofile-theme">
      <div className="unit-header">
        <div>
          <div className="unit-page-label">Performance</div>
          <h2>Performance Profiles</h2>
          <p>Configure which metrics count toward a composite score, and for whom — never auto-activated.</p>
        </div>
        <div className="unit-header-actions">
          <Button className="unit-btn unit-btn-light" onClick={fetchProfiles}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            Refresh
          </Button>
          <Can moduleKey="performance_profiles" actionKey="create">
            <Button className="unit-btn unit-btn-primary" onClick={() => setShowModal(true)}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              New Profile
            </Button>
          </Can>
        </div>
      </div>

      <div className="unit-card mb-3 perfprofile-preview-card">
        <div className="unit-toolbar">
          <div>
            <h5>
              <i className="fa-solid fa-route perfprofile-list-icon" aria-hidden="true"></i>
              Preview Resolution
            </h5>
            <p>Check which profile would apply to a given user/context.</p>
          </div>
        </div>
        <div className="perfprofile-preview-grid">
          <Form.Control size="sm" placeholder="User ID" value={previewCtx.user_id} onChange={(e) => setPreviewCtx({ ...previewCtx, user_id: e.target.value })} />
          <Form.Control size="sm" placeholder="Role ID" value={previewCtx.role_id} onChange={(e) => setPreviewCtx({ ...previewCtx, role_id: e.target.value })} />
          <Form.Control size="sm" placeholder="Department ID" value={previewCtx.department_id} onChange={(e) => setPreviewCtx({ ...previewCtx, department_id: e.target.value })} />
          <Form.Control size="sm" placeholder="Project ID" value={previewCtx.project_id} onChange={(e) => setPreviewCtx({ ...previewCtx, project_id: e.target.value })} />
          <Form.Control size="sm" placeholder="Module ID" value={previewCtx.project_module_id} onChange={(e) => setPreviewCtx({ ...previewCtx, project_module_id: e.target.value })} />
          <Button size="sm" variant="outline-primary" className="perfprofile-preview-btn" onClick={runPreview}>Preview</Button>
        </div>
        {previewResult && (
          <div className="px-3 pb-3 small perfprofile-preview-result">
            Resolution tier: <Badge bg="info">{previewResult.resolutionTier}</Badge>{" "}
            {previewResult.resolvedProfile ? `→ ${previewResult.resolvedProfile.name} (${previewResult.resolvedProfile.code})` : "→ No profile applies (raw dashboard only)"}
          </div>
        )}
      </div>

      <div className="unit-stats-row">
        <div className="unit-stat-card">
          <div className="perfprofile-stat-icon perfprofile-stat-icon-primary">
            <i className="fa-solid fa-sliders" aria-hidden="true"></i>
          </div>
          <div className="perfprofile-stat-text">
            <span>Total Profiles</span>
            <strong>{stats.total}</strong>
          </div>
        </div>
        <div className="unit-stat-card">
          <div className="perfprofile-stat-icon perfprofile-stat-icon-success">
            <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
          </div>
          <div className="perfprofile-stat-text">
            <span>Active</span>
            <strong>{stats.active}</strong>
          </div>
        </div>
        <div className="unit-stat-card">
          <div className="perfprofile-stat-icon perfprofile-stat-icon-accent">
            <i className="fa-solid fa-ranking-star" aria-hidden="true"></i>
          </div>
          <div className="perfprofile-stat-text">
            <span>Ranking Enabled</span>
            <strong>{stats.ranking}</strong>
          </div>
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div>
            <h5>
              <i className="fa-solid fa-gauge-high perfprofile-list-icon" aria-hidden="true"></i>
              Profile List
            </h5>
            <p>Search by code or name, then manage metrics or activation for a profile.</p>
          </div>
        </div>
        <div className="unit-filter-row">
          <InputGroup className="unit-search" style={{ width: 220 }}>
            <InputGroup.Text aria-hidden="true">
              <i className="fa-solid fa-magnifying-glass"></i>
            </InputGroup.Text>
            <Form.Control placeholder="Search code or name..." aria-label="Search code or name" value={search} onChange={(e) => setSearch(e.target.value)} />
          </InputGroup>
          {search && <Button className="unit-clear-filters-btn" onClick={() => setSearch("")}>Clear Filters</Button>}
        </div>
        <div className="unit-table-wrapper">
          {loading ? <div className="unit-loader"><Spinner animation="border" /></div> : (
            <Table responsive className="unit-table">
              <thead><tr><th>Code</th><th>Name</th><th>Target</th><th>Ranking</th><th>Status</th><th className="text-end">Actions</th></tr></thead>
              <tbody>
                {visibleProfiles.length ? visibleProfiles.map((p) => (
                  <tr key={p.id}>
                    <td>{p.code}</td>
                    <td>{p.name}</td>
                    <td className="small text-muted perfprofile-col-target">
                      {p.user_id ? `User #${p.user_id}` : p.project_module_id ? `Module #${p.project_module_id}` : p.project_id ? `Project #${p.project_id}` : p.role_id && p.department_id ? `Role #${p.role_id} + Dept #${p.department_id}` : p.role_id ? `Role #${p.role_id}` : p.department_id ? `Dept #${p.department_id}` : "Default (all)"}
                    </td>
                    <td>{p.allow_ranking ? <Badge bg="warning" text="dark">Enabled</Badge> : <Badge bg="light" text="dark">Disabled</Badge>}</td>
                    <td><Badge className="perfprofile-status-badge" bg={p.is_active ? "success" : "secondary"}>{p.is_active ? "Active" : "Inactive"}</Badge></td>
                    <td>
                      <div className="unit-actions" style={{ flexWrap: "wrap" }}>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => setEditingProfileId(editingProfileId === p.id ? null : p.id)}
                          title={editingProfileId === p.id ? "Close metrics editor" : "Edit metrics"}
                          aria-label={editingProfileId === p.id ? `Close metrics editor for ${p.name || "profile"}` : `Edit metrics for ${p.name || "profile"}`}
                        >
                          <i className={`fa-solid ${editingProfileId === p.id ? "fa-xmark" : "fa-sliders"}`} aria-hidden="true"></i>
                          {editingProfileId === p.id ? "Close" : "Metrics"}
                        </Button>
                        <Can moduleKey="performance_profiles" actionKey="activate">
                          <Button
                            size="sm"
                            variant={p.is_active ? "outline-secondary" : "outline-success"}
                            onClick={() => toggleActive(p)}
                            title={p.is_active ? "Deactivate profile" : "Activate profile"}
                            aria-label={p.is_active ? `Deactivate ${p.name || "profile"}` : `Activate ${p.name || "profile"}`}
                          >
                            <i className={`fa-solid ${p.is_active ? "fa-pause" : "fa-play"}`} aria-hidden="true"></i>
                            {p.is_active ? "Deactivate" : "Activate"}
                          </Button>
                        </Can>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6">
                      <EmptyState
                        icon={FaUserCog}
                        title="No performance profiles found"
                        message={search ? "Try another search." : "Create your first performance profile to get started."}
                        action={search ? (
                          <Button className="unit-btn unit-btn-light" onClick={() => setSearch("")}>Clear Filters</Button>
                        ) : (
                          <Can moduleKey="performance_profiles" actionKey="create">
                            <Button className="unit-btn unit-btn-primary" onClick={() => setShowModal(true)}>
                              <i className="fa-solid fa-plus" aria-hidden="true"></i>
                              New Profile
                            </Button>
                          </Can>
                        )}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>
        {editingProfileId && (
          <div className="p-3 border-top perfprofile-inline-editor">
            <PerformanceProfileEditor profileId={editingProfileId} onSaved={fetchProfiles} />
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg" className="unit-modal">
        <Modal.Header closeButton><Modal.Title>New Performance Profile</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}><Form.Label>Code</Form.Label><Form.Control value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Col>
            <Col md={6}><Form.Label>Name</Form.Label><Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Col>
            <Col md={12}><Form.Label>Description</Form.Label><Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Col>
            <Col md={3}><Form.Label>User ID (override, optional)</Form.Label><Form.Control type="number" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} /></Col>
            <Col md={3}><Form.Label>Role ID (optional)</Form.Label><Form.Control type="number" value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} /></Col>
            <Col md={3}><Form.Label>Department ID (optional)</Form.Label><Form.Control type="number" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} /></Col>
            <Col md={3}><Form.Label>Project ID (optional)</Form.Label><Form.Control type="number" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} /></Col>
            <Col md={3}><Form.Label>Project Module ID (optional)</Form.Label><Form.Control type="number" value={form.project_module_id} onChange={(e) => setForm({ ...form, project_module_id: e.target.value })} /></Col>
            <Col md={9} className="d-flex align-items-end">
              <Form.Check type="checkbox" label="Allow ranking (leaderboard) for this profile" checked={form.allow_ranking} onChange={(e) => setForm({ ...form, allow_ranking: e.target.checked })} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button className="unit-btn unit-btn-primary" onClick={handleCreate} disabled={saving}>{saving ? "Saving..." : "Create"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
